import type { SpreadDefinition } from './core';
import { createSpreadDefinition } from '../internal/builders';

export const SPECCHIO_DI_LILITH_SPREAD_DEFINITION: SpreadDefinition = createSpreadDefinition({
  id: 'specchio-di-lilith',
  name: 'Specchio di Lilith',
  slug: 'specchio-di-lilith',
  category: 'lilith',
  description: 'Sistema proprietario a nove carte: desiderio, ferita, richiamo, catena e integrazione sull’asse di Lilith.',
  ritual_note: 'È una discesa rituale: non usarla per curiosità rapida ma per una soglia reale.',
  opening_invocation: 'Specchio di Lilith, mostra ciò che seduce, ferisce, incatena e libera.',
  closing_words: 'Richiudo lo specchio, raccolgo il desiderio e porto a terra solo ciò che posso sostenere.',
  difficulty: 'initiatic',
  is_lilith: true,
  tree_of_life_mapping: false,
  family: 'mirror',
  intent: ['lilith', 'shadow', 'relationship'],
  semantic_tags: ['shadow-work', 'initiatic', 'ritual', 'relational'],
  layout_id: 'lilith-mirror-9',
  position_specs: [
    {
      role_id: 'mask',
      name: 'Maschera sociale',
      description: 'Quale volto mostri per restare leggibile o desiderabile?',
      qliphah_correspondence: 'Nahemoth',
      overlay_narrative_note: 'La superficie protegge ma può irrigidirsi in personaggio.',
    },
    {
      role_id: 'desire',
      name: 'Desiderio profondo',
      description: 'Che cosa brama davvero il tuo nucleo, sotto il copione?',
      qliphah_correspondence: 'Gamaliel',
      element_affinity: 'Acqua',
      overlay_narrative_note: 'Il desiderio non chiede permesso; chiede lingua e responsabilità.',
    },
    {
      role_id: 'wound',
      name: 'Ferita originaria',
      description: 'Quale lesione antica continua a selezionare esperienze e reazioni?',
      qliphah_correspondence: 'Samael',
      element_affinity: 'Aria',
      overlay_narrative_note: 'La ferita prende voce quando smette di travestirsi da destino.',
    },
    {
      role_id: 'temptation',
      name: 'Richiamo',
      description: 'Quale seduzione destabilizza e insieme rivela?',
      qliphah_correspondence: 'A_arab_Zaraq',
      element_affinity: 'Fuoco',
      overlay_narrative_note: 'La tentazione segnala spesso il luogo in cui il potere è ancora esterno.',
    },
    {
      role_id: 'repressed_power',
      name: 'Potere represso',
      description: 'Quale forza è stata compressa fino a diventare paura o eccesso?',
      qliphah_correspondence: 'Thagirion',
      overlay_narrative_note: 'Il centro non chiede purezza, chiede disponibilità a reggere la propria potenza.',
    },
    {
      role_id: 'chain',
      name: 'Catena da spezzare',
      description: 'Quale schema perpetua dipendenza, vergogna o ripetizione?',
      qliphah_correspondence: 'Golachab',
      element_affinity: 'Terra',
      overlay_narrative_note: 'La catena non cade con la volontà pura, ma con un atto di riconoscimento preciso.',
    },
    {
      role_id: 'truth',
      name: 'Verità erotico-spirituale',
      description: 'Quale verità non può più essere negata senza costo?',
      qliphah_correspondence: 'Gha_agsheblah',
      overlay_narrative_note: 'Qui il desiderio smette di essere consumo e diventa conoscenza incarnata.',
    },
    {
      role_id: 'ritual_action',
      name: 'Azione rituale',
      description: 'Quale gesto concreto o simbolico apre il passaggio successivo?',
      qliphah_correspondence: 'Satariel',
      overlay_narrative_note: 'Il rito serve a contenere la forza emersa, non a teatralizzarla.',
    },
    {
      role_id: 'integration',
      name: 'Esito archetipico',
      description: 'Quale forma di integrazione è possibile se il processo viene onorato?',
      qliphah_correspondence: 'Ghagiel',
      overlay_narrative_note: 'L’integrazione non chiude la tensione: le dà una forma vivibile.',
    },
  ],
  keywords: ['lilith', 'specchio', 'desiderio', 'ombra', 'rituale'],
  recommended_for_questions: [
    'Quale verità desiderante sto contenendo male?',
    'Dove la mia ombra erotico-spirituale sta chiedendo forma?',
  ],
  contraindications: [
    'Non consigliata per domanda rapidissima o daily.',
    'Non usarla come gioco se non c’è disponibilità a sostenere il materiale che emerge.',
  ],
  focus_mode_ids: [
    'lilith-ombra',
    'lilith-desiderio',
    'lilith-potere',
    'lilith-relazione',
    'lilith-iniziazione',
  ],
  ritual_intensity: 5,
});

export const SPECCHIO_DI_LILITH_LITE_SPREAD_DEFINITION: SpreadDefinition = createSpreadDefinition({
  id: 'specchio-di-lilith-lite',
  name: 'Specchio di Lilith Lite',
  slug: 'specchio-di-lilith-lite',
  category: 'lilith',
  description: 'Versione mobile-first a cinque carte del sistema Lilith, pensata per accessi rapidi ma non superficiali.',
  ritual_note: 'Rapida non significa leggera: formula una sola domanda e non aprire più varchi insieme.',
  opening_invocation: 'Specchio breve, mostra il nucleo e non il rumore.',
  closing_words: null,
  difficulty: 'advanced',
  is_lilith: true,
  tree_of_life_mapping: false,
  family: 'line',
  intent: ['lilith', 'shadow'],
  semantic_tags: ['shadow-work', 'ritual', 'initiatic'],
  layout_id: 'line-5',
  position_specs: [
    {
      role_id: 'mask',
      name: 'Maschera',
      description: 'Quale facciata domina il campo?',
      qliphah_correspondence: 'Nahemoth',
      overlay_narrative_note: 'Il visibile è l’ingresso, non il cuore.',
    },
    {
      role_id: 'desire',
      name: 'Desiderio',
      description: 'Che cosa chiama davvero da sotto la superficie?',
      qliphah_correspondence: 'Gamaliel',
      overlay_narrative_note: 'Il desiderio chiede lingua, non anestesia.',
    },
    {
      role_id: 'wound',
      name: 'Ferita',
      description: 'Quale taglio riaccende il tema?',
      qliphah_correspondence: 'Samael',
      overlay_narrative_note: 'Ogni richiamo passa ancora dalla ferita non nominata.',
    },
    {
      role_id: 'repressed_power',
      name: 'Potere',
      description: 'Quale forza trattenuta potrebbe cambiare il quadro?',
      qliphah_correspondence: 'Thagirion',
      overlay_narrative_note: 'Il potere represso ritorna sempre sotto forma di eccesso o autosabotaggio.',
    },
    {
      role_id: 'integration',
      name: 'Integrazione',
      description: 'Che forma può prendere il processo senza rompersi?',
      qliphah_correspondence: 'Ghagiel',
      overlay_narrative_note: 'L’integrazione vera è misurabile in gesto, parola, limite.',
    },
  ],
  keywords: ['lilith-lite', 'mobile', 'ombra', 'desiderio'],
  recommended_for_questions: [
    'Mi serve entrare nel tema di Lilith senza aprire una struttura completa.',
    'Qual è il nucleo ombra-desiderio in gioco adesso?',
  ],
  contraindications: ['Non ideale se il processo richiede una discesa completa o una mappa relazionale articolata.'],
  focus_mode_ids: [],
  ritual_intensity: 4,
});

export const LILITH_SPREAD_DEFINITIONS: SpreadDefinition[] = [
  SPECCHIO_DI_LILITH_SPREAD_DEFINITION,
  SPECCHIO_DI_LILITH_LITE_SPREAD_DEFINITION,
];
